import { useState, useEffect, useCallback } from 'react'
import { schoolPatternApi, type SchoolPatternsResponse, type CreatePatternRequest } from '@/services/api/patternPipeline'
import { Card, Button, LoadingSpinner, Input, Select, Modal } from '@/components/common'
import { Plus, Trash2, Save, X } from 'lucide-react'

const FIELD_OPTIONS = [
  { value: 'room', label: 'Room' },
  { value: 'class_name', label: 'Class' },
  { value: 'subject', label: 'Subject' },
  { value: 'teacher_name', label: 'Teacher Name' },
  { value: 'teacher_code', label: 'Teacher Code' },
  { value: 'first_name', label: 'First Name' },
  { value: 'family_name', label: 'Family Name' },
  { value: 'time_slot', label: 'Time Slot' },
  { value: 'ignore', label: 'Ignore' },
]

const MATCH_MODE_OPTIONS = [
  { value: 'prefix', label: 'Prefix (starts with)' },
  { value: 'full', label: 'Full (exact match)' },
  { value: 'search', label: 'Search (anywhere)' },
]

export function SuperadminPatternEditorPage() {
  const [data, setData] = useState<SchoolPatternsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'single' | 'multi' | 'orphan' | 'ignore'>('single')
  const [showAddModal, setShowAddModal] = useState(false)
  const [newPattern, setNewPattern] = useState<CreatePatternRequest>({
    regex: '',
    field: 'room',
    match_mode: 'prefix',
    flags: ['IGNORECASE'],
    priority: 0,
  })
  const [ignoreTokensText, setIgnoreTokensText] = useState('')
  const [orphanThreshold, setOrphanThreshold] = useState(2)

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await schoolPatternApi.getPatterns()
      setData(result)
      setIgnoreTokensText(result.ignore_tokens.join('\n'))
      setOrphanThreshold(result.orphan_threshold)
    } catch (err: any) {
      setError(err.message || 'Failed to load patterns')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleAdd = async () => {
    if (!newPattern.regex.trim()) {
      setError('Regex is required')
      return
    }
    setSaving(true)
    try {
      await schoolPatternApi.createSingle(newPattern)
      setSuccess('Pattern added and queued for analysis')
      setShowAddModal(false)
      setNewPattern({ regex: '', field: 'room', match_mode: 'prefix', flags: ['IGNORECASE'], priority: 0 })
      await loadData()
    } catch (err: any) {
      setError(err.message || 'Failed to add pattern')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (schoolIndex: number | null | undefined) => {
    if (schoolIndex == null) return
    if (!confirm('Delete this pattern?')) return
    setSaving(true)
    try {
      await schoolPatternApi.deleteSingle(schoolIndex)
      setSuccess('Pattern deleted')
      await loadData()
    } catch (err: any) {
      setError(err.message || 'Failed to delete pattern')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveIgnoreTokens = async () => {
    setSaving(true)
    try {
      const tokens = ignoreTokensText.split('\n').map((t) => t.trim()).filter(Boolean)
      await schoolPatternApi.updateIgnoreTokens(tokens)
      setSuccess('Ignore tokens updated')
      await loadData()
    } catch (err: any) {
      setError(err.message || 'Failed to update ignore tokens')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveThreshold = async () => {
    setSaving(true)
    try {
      await schoolPatternApi.updateOrphanThreshold(orphanThreshold)
      setSuccess('Orphan threshold updated')
      await loadData()
    } catch (err: any) {
      setError(err.message || 'Failed to update threshold')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!data) return null

  const patterns = activeTab === 'ignore' ? [] : data[activeTab]

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pattern Editor</h1>
          <p className="text-sm text-gray-500 mt-1">Manage regex patterns for token auto-matching</p>
        </div>
        {activeTab === 'single' && (
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-1.5" />
            Add Pattern
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex justify-between items-center">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-500 hover:text-green-700">&times;</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 mb-6 border-b">
        {[
          { key: 'single' as const, label: 'Single Token' },
          { key: 'multi' as const, label: 'Multi-Token' },
          { key: 'orphan' as const, label: 'Orphan Detection' },
          { key: 'ignore' as const, label: 'Ignore Tokens' },
        ].map((tab) => (
          <button
            key={tab.key}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              activeTab === tab.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.key !== 'ignore' && (
              <span className="ml-1.5 text-xs text-gray-400">({data[tab.key].length})</span>
            )}
          </button>
        ))}
      </div>

      {/* Pattern List */}
      {activeTab !== 'ignore' ? (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-2.5 px-4 font-medium text-gray-600 w-1/2">Regex</th>
                <th className="text-left py-2.5 px-4 font-medium text-gray-600">Field</th>
                <th className="text-left py-2.5 px-4 font-medium text-gray-600">Mode</th>
                <th className="text-left py-2.5 px-4 font-medium text-gray-600">Priority</th>
                <th className="text-left py-2.5 px-4 font-medium text-gray-600">Source</th>
                <th className="text-right py-2.5 px-4 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {patterns.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    No patterns in this category
                  </td>
                </tr>
              ) : (
                patterns.map((p, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-4 font-mono text-xs truncate max-w-[300px]" title={p.regex}>
                      {p.regex}
                    </td>
                    <td className="py-2 px-4">
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs">
                        {p.field}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-gray-600">{p.match_mode}</td>
                    <td className="py-2 px-4 text-gray-600">{p.priority}</td>
                    <td className="py-2 px-4 text-gray-600">{p.source}</td>
                    <td className="py-2 px-4 text-right">
                      {activeTab === 'single' && p.school_index != null ? (
                        <button
                          className="text-red-500 hover:text-red-700 p-1"
                          onClick={() => handleDelete(p.school_index)}
                          disabled={saving}
                          aria-label="Delete pattern"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      ) : p.school_index == null ? (
                        <span className="text-xs text-gray-400">geerbt</span>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Ignore Tokens */}
          <Card className="p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Ignore Tokens</h3>
            <p className="text-xs text-gray-500 mb-3">One token per line. These tokens will be automatically ignored during matching.</p>
            <textarea
              className="w-full border border-gray-300 rounded-lg p-3 text-sm font-mono resize-y focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={8}
              value={ignoreTokensText}
              onChange={(e) => setIgnoreTokensText(e.target.value)}
            />
            <div className="flex justify-end mt-3">
              <Button variant="primary" isLoading={saving} onClick={handleSaveIgnoreTokens}>
                <Save className="w-4 h-4 mr-1.5" />
                Save Ignore Tokens
              </Button>
            </div>
          </Card>

          {/* Orphan Threshold */}
          <Card className="p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Orphan Detection Threshold</h3>
            <p className="text-xs text-gray-500 mb-3">
              Minimum number of cells an unmatched token must co-occur with a matched token to be auto-classified.
              Set to 0 to disable orphan detection.
            </p>
            <div className="flex items-center gap-4">
              <input
                type="number"
                min={0}
                max={10}
                className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={orphanThreshold}
                onChange={(e) => setOrphanThreshold(parseInt(e.target.value) || 0)}
              />
              <Button variant="primary" isLoading={saving} onClick={handleSaveThreshold}>
                <Save className="w-4 h-4 mr-1.5" />
                Save Threshold
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Add Pattern Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add Pattern" size="md">
        <div className="space-y-4">
          <Input
            label="Regex Pattern"
            placeholder="^bsd[-.]?[A-Za-z0-9]+"
            value={newPattern.regex}
            onChange={(e) => setNewPattern({ ...newPattern, regex: e.target.value })}
          />
          <Select
            label="Field Type"
            options={FIELD_OPTIONS}
            value={newPattern.field}
            onChange={(e) => setNewPattern({ ...newPattern, field: e.target.value })}
          />
          <Select
            label="Match Mode"
            options={MATCH_MODE_OPTIONS}
            value={newPattern.match_mode}
            onChange={(e) => setNewPattern({ ...newPattern, match_mode: e.target.value })}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <input
              type="number"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={newPattern.priority}
              onChange={(e) => setNewPattern({ ...newPattern, priority: parseInt(e.target.value) || 0 })}
            />
            <p className="text-xs text-gray-500 mt-1">Higher priority patterns are matched first. Use negative values for catch-all patterns.</p>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>
              <X className="w-4 h-4 mr-1.5" />
              Cancel
            </Button>
            <Button variant="primary" isLoading={saving} onClick={handleAdd}>
              <Plus className="w-4 h-4 mr-1.5" />
              Add Pattern
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
